import type { JSX } from 'react';
import { PatientIndicator, type PatientContextState } from './PatientIndicator';
import { ListeningIndicator } from './ListeningIndicator';
import type { VoiceSnapshot } from '../voice/controller';
import { useT, useUiLanguage } from '../i18n/useT';
import { updateLocalSettings } from '../settings/local';

export interface TopBarProps {
  context: PatientContextState;
  voiceState: VoiceSnapshot;
  voiceOutputEnabled: boolean;
  speaking: boolean;
  onPatientClick: () => void;
  onToggleVoiceOutput: () => void;
  onStopSpeaking: () => void;
  onOpenSettings: () => void;
  onCancelVoice: () => void;
  onSignOut: () => void;
  tenantName?: string;
  displayName?: string;
}

export function TopBar(props: TopBarProps): JSX.Element {
  const t = useT();
  const lang = useUiLanguage();
  return (
    <header className="topbar">
      <div className="topbar-row">
        <div className="brand">
          <img className="brand-logo" src="/ampshift-logo.svg" alt="" aria-hidden="true" width={34} height={34} />
          <div className="brand-text">
            <span className="brand-name">{t('productName')}</span>
            {props.tenantName ? <span className="muted small">{props.tenantName}</span> : null}
          </div>
        </div>
        <PatientIndicator context={props.context} onClick={props.onPatientClick} />
        <div className="topbar-actions">
          <button
            type="button"
            className={props.voiceOutputEnabled ? 'icon-btn on' : 'icon-btn'}
            aria-pressed={props.voiceOutputEnabled}
            title={props.voiceOutputEnabled ? t('voiceOutputOn') : t('voiceOutputOff')}
            onClick={props.onToggleVoiceOutput}
            data-testid="voice-output-toggle"
          >
            {props.voiceOutputEnabled ? '🔊' : '🔇'}
          </button>
          {props.speaking ? (
            <button type="button" className="icon-btn speaking" title={t('stopSpeaking')} onClick={props.onStopSpeaking}>
              ⏹
            </button>
          ) : null}
          <button type="button" className="icon-btn" title={t('settings')} aria-label={t('settings')} onClick={props.onOpenSettings} data-testid="settings-button">
            ⚙
          </button>
          <button
            type="button"
            className="icon-btn lang"
            title={t('language')}
            aria-label={t('language')}
            onClick={() => updateLocalSettings({ uiLanguage: lang === 'en' ? 'ar' : 'en' })}
            data-testid="language-toggle"
          >
            {lang === 'en' ? 'ع' : 'EN'}
          </button>
          <button type="button" className="btn btn-small signout" onClick={props.onSignOut} data-testid="sign-out" aria-label={t('signOut')} title={props.displayName ? `${t('signOut')} · ${props.displayName}` : t('signOut')}>
            <span className="signout-icon" aria-hidden="true">⏻</span>
            <span className="signout-text">{t('signOut')}</span>
            {props.displayName ? <span className="muted small signout-name"> · {props.displayName}</span> : null}
          </button>
        </div>
      </div>
      <ListeningIndicator state={props.voiceState} onCancel={props.onCancelVoice} />
    </header>
  );
}
